import api from "../../config/api";
import { defaultPagination } from "@/store/common/pagination";

export default {
    state: {
        messagingYearlyTotal: "",
        messagingDailyTotal: "",
        messagingSummary: [],
        messagingSummaryPagination: defaultPagination(),
        messagingCenterInfo: "",
        messagingPersons: [],
        sendMessageResponse: [],
        aiPageData: [],
    },
    getters: {
        stMessagingYearlyTotals: state => state.messagingYearlyTotal,
        stMessagingDailyTotals: state => state.messagingDailyTotal,
        stMessagingSummary: state => state.messagingSummary,
        stMessagingSummaryPagination: state => state.messagingSummaryPagination,
        stMessagingCenterInfo: (state) => state.messagingCenterInfo,
        stMessagingPersons: (state) => state.messagingPersons,
        stAIPageData: (state) => state.aiPageData,
    },
    mutations: {
        setMessagingYearlyTotals(state, payload) {
            state.messagingYearlyTotal = payload;
        },
        setMessagingDailyTotals(state, payload) {
            state.messagingDailyTotal = payload;
        },
        setMessagingSummary(state, payload) {
            state.messagingSummary = payload;
        },
        SET_MESSAGING_SUMMARY_PAGINATION(state, paginationData) {
            state.messagingSummaryPagination = {
                ...state.messagingSummaryPagination,
                ...paginationData,
            };
        },
        setMessagingCenterInfo(state, payload) {
            state.messagingCenterInfo = payload;
        },
        setMessagingPersons(state, payload) {
            state.messagingPersons = payload;
        },
        setSendMessageResponse(state, payload) {
            state.sendMessageResponse = payload;
        },
        setAIPageData(state, payload) {
            state.aiPageData = payload;
        },
    },
    actions: {
        async fetchMessagingYearlyTotals({ commit }, locationID) {
            try {
                await api.post(`/managelocationajax`, {
                    cmd: "getmessagingmonthyeartotals",
                    locationid: locationID
                })
                    .then((response) => {
                        commit('setMessagingYearlyTotals', response.data.output);
                    })
                    .catch((error) => {
                        console.log("Error in getting Messaging Units", error);
                    });
            } catch (error) {
                console.error('Error in getting Messaging Units:', error);
            }
        },
        async fetchMessagingDailyTotals({ commit }, locationID) {
            if (!locationID) {
                console.error('fetchMessagingDailyTotals: locationID is required');
                commit('setMessagingDailyTotals', []);
                return;
            }
            try {
                await api.post(`/managelocationajax`, {
                    cmd: "getlocationmessagescountbyday",
                    locationid: locationID
                })
                    .then((response) => {
                        // Ensure we set an array, even if response.data.output is not an array
                        const output = response.data?.output;
                        commit('setMessagingDailyTotals', Array.isArray(output) ? output : []);
                    })
                    .catch((error) => {
                        console.log("Error in getting Messaging Units", error);
                        commit('setMessagingDailyTotals', []);
                    });
            } catch (error) {
                console.error('Error in getting Messaging Units:', error);
                commit('setMessagingDailyTotals', []);
            }
        },
        async fetchMessagingSummary({ commit }, { locationID, page = 1, pageSize = 10, search = "", messageTypes = [] }) {
            try {
                console.log('fetchMessagingSummary called with:', { locationID, page, pageSize, search, messageTypes });
                
                const payload = {
                    cmd: "getmessagessummary",
                    locationid: locationID,
                    page_number: page,
                    page_size: pageSize,
                    search: search || ""
                };

                if (messageTypes.length > 0) {
                    payload.message_type_filter = messageTypes.join(',');
                }

                const response = await api.post(`/managelocationajax`, payload);
                
                console.log('fetchMessagingSummary response:', response.data);
                
                const output = response.data?.output || [];
                const pagination = response.data?.pagination;
                
                commit('setMessagingSummary', output);
                
                if (pagination) {
                    console.log('Updating pagination state:', pagination);
                    // Ensure pageSize matches one of the available options (5, 10, 25, 50, 100)
                    // If not, use the closest option >= the returned page_size, or default to 10
                    const pageSizeOptions = [5, 10, 25, 50, 100];
                    const returnedPageSize = pagination.page_size;
                    let pageSize = returnedPageSize;
                    
                    if (!pageSizeOptions.includes(returnedPageSize)) {
                        // Find closest option >= returnedPageSize, or default to 10
                        pageSize = pageSizeOptions.find(opt => opt >= returnedPageSize) || 10;
                        console.log(`Page size ${returnedPageSize} not in options, using ${pageSize}`);
                    }
                    
                    commit('SET_MESSAGING_SUMMARY_PAGINATION', {
                        currentPage: pagination.current_page,
                        totalPages: pagination.total_pages,
                        totalRecords: pagination.total_records,
                        pageSize: pageSize,
                        hasPrevious: pagination.has_previous,
                        hasNext: pagination.has_next,
                    });
                } else {
                    console.warn('No pagination data in response');
                }
            } catch (error) {
                console.error('Error in getting Messaging Summary:', error);
                commit('setMessagingSummary', []);
                throw error;
            }
        },
        async fetchMessagingCenterInfo({ commit }, locationID) {
            try {
                const response = await api.get(
                    `/sendmessage/messaging-center-info?locationid=${locationID}`
                );
                commit("setMessagingCenterInfo", response.data);
                return response.data;
            } catch (error) {
                console.error("Error fetching Messaging Center Info:", error);
                throw error;
            }
        },
        async fetchMessagingPersons({ commit }, { messageTarget, idsArray }) {
            try {
                console.log("messageTarget", messageTarget);
                const response = await api.post(`/sendmessage/get-persons`, {
                    cmd: messageTarget,
                    idsarray: idsArray,
                });

                // assuming response.data contains person list
                commit("setMessagingPersons", response.data);
                return response.data;
            } catch (error) {
                console.error("Error fetching Messaging Persons:", error);
                throw error;
            }
        },
        async sendMessage({ commit }, payload) {
            payload = { cmd: "sendmessage", ...payload };
            // Base64 encode the HTML message body to prevent AWS WAF from
            // blocking the request (WAF sees raw HTML tags as potential XSS).
            if (payload.message) {
                payload.message = btoa(unescape(encodeURIComponent(payload.message)));
                payload.message_encoding = 'base64';
            }
            try {
                const response = await api.post(`/sendmessageajax`, payload);
                commit("setSendMessageResponse", response.data);
                return response.data;
            } catch (error) {
                console.error("Error sending message:", error);
                throw error;
            }
        },
        async fetchActivityInstances({ commit }, {activityWorkgroupId}) {
            try {
                const response = await api.get(`/sendmessage/page-data?cmd=ai&id=${activityWorkgroupId}`);
                commit("setAIPageData", response.data);
                return response.data;
            } catch (error) {
                console.error("Error fetching AI Page Data:", error);
                throw error;
            }
        },
        async fetchNewUsersData({ commit }, locationId) {
            try {
                const response = await api.get(`/sendmessage/page-data?cmd=newusers&id=${locationId}`);
                const data = response.data;
                // Map persons_list into the same shape used by messagingPersons
                const persons = (data.persons_list || []).map(p => ({
                    ...p,
                    name: p.name || `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Unknown'
                }));
                commit("setMessagingPersons", { persons });
                return data;
            } catch (error) {
                console.error("Error fetching new users data:", error);
                throw error;
            }
        },
    }
};
